import { useEffect, useRef, useState } from 'react';
import './App.css';
import { config, INIT_PROMPT, SYSTEM_PROMPT } from './config';
import { playCommandSound, playSuccessSound, playGameOverSound, playErrorSound, playTypeSound, initAudio } from './sounds';

// Typewriter component for narrative text
function Typewriter({ text, speed = 20, onComplete }) {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);

  // Keep ref updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    setDisplayedText('');
    setIsComplete(false);
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(text.slice(0, i + 1));
        if (i % 3 === 0) playTypeSound();
        i++;
      } else {
        clearInterval(timer);
        setIsComplete(true);
        if (onCompleteRef.current) onCompleteRef.current();
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed]); // Removed onComplete from deps

  const handleClick = () => {
    if (!isComplete) {
      setDisplayedText(text);
      setIsComplete(true);
      if (onCompleteRef.current) onCompleteRef.current();
    }
  };

  return (
    <span onClick={handleClick} style={{ cursor: isComplete ? 'default' : 'pointer' }}>
      {displayedText}
      {!isComplete && <span className="cursor">▌</span>}
    </span>
  );
}

export default function App() {
  const [screen, setScreen] = useState('intro');
  const [article, setArticle] = useState('');
  const [messages, setMessages] = useState([]);
  const [gameState, setGameState] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [turnCount, setTurnCount] = useState(0);
  const [gameTitle, setGameTitle] = useState('');
  const [sourceUrl, setSourceUrl] = useState(null);
  const [narrativeComplete, setNarrativeComplete] = useState(false);
  const terminalRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const articleId = params.get('id');
    if (articleId) loadArticleFromApi(articleId);
  }, []);

  useEffect(() => {
    if (terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [gameState]);

  // Focus input when narrative is complete
  useEffect(() => {
    if (narrativeComplete && inputRef.current) {
      inputRef.current.focus();
    }
  }, [narrativeComplete]);

  const loadArticleFromApi = async (id) => {
    setScreen('loading');
    try {
      const response = await fetch(config.apiBaseUrl + '/article/' + id);
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setArticle(data.content);
      setSourceUrl(data.sourceUrl || null);
      await startGameWithArticle(data.content, true); // true = from article ID
    } catch (err) {
      playErrorSound();
      setError(err.message || 'Kunde inte ladda artikeln');
      setScreen('intro');
    }
  };

  const callClaude = async (userMessage, isInit = false) => {
    const newMessages = isInit
      ? [{ role: 'user', content: INIT_PROMPT + userMessage }]
      : [...messages, { role: 'user', content: userMessage }];

    const response = await fetch(config.apiBaseUrl + '/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system: SYSTEM_PROMPT,
        messages: newMessages
      })
    });

    const data = await response.json();
    if (data.error) throw new Error(data.error);

    const assistantMessage = data.content[0].text;
    let parsed;
    try {
      const jsonMatch = assistantMessage.match(/```json\n?([\s\S]*?)\n?```/);
      const jsonStr = jsonMatch ? jsonMatch[1] : assistantMessage;
      parsed = JSON.parse(jsonStr.trim());
    } catch (e) {
      const start = assistantMessage.indexOf('{');
      const end = assistantMessage.lastIndexOf('}');
      if (start !== -1 && end !== -1) {
        parsed = JSON.parse(assistantMessage.slice(start, end + 1));
      } else {
        throw new Error('Kunde inte tolka spelets svar');
      }
    }
    setMessages([...newMessages, { role: 'assistant', content: assistantMessage }]);
    return parsed;
  };

  const startGameWithArticle = async (articleContent, fromArticleId = false) => {
    setLoading(true);
    setError(null);
    setScreen('loading');
    setNarrativeComplete(false);
    try {
      const state = await callClaude(articleContent, true);
      setGameState(state);
      setGameTitle(state.title || 'VK Quest');
      setTurnCount(1);
      // If loaded via article ID, show ready screen to get user interaction for audio
      setScreen(fromArticleId ? 'ready' : 'game');
      if (!fromArticleId) playSuccessSound();
    } catch (err) {
      playErrorSound();
      setError(err.message);
      setScreen('article');
    } finally {
      setLoading(false);
    }
  };

  const startFromReady = () => {
    initAudio();
    playSuccessSound();
    setScreen('game');
  };

  const startGame = async () => {
    if (!article.trim()) return;
    playCommandSound();
    await startGameWithArticle(article);
  };

  const submitCommand = async () => {
    if (!input.trim() || loading) return;
    playCommandSound();
    const command = input;
    setInput('');
    setLoading(true);
    setError(null);
    setNarrativeComplete(false);
    try {
      const prompt = turnCount > 15
        ? '[Tur ' + turnCount + ' - Överväg att avsluta snart]\nSpelarens kommando: ' + command
        : 'Spelarens kommando: ' + command;
      const state = await callClaude(prompt);
      setGameState(state);
      setTurnCount(t => t + 1);
      if (state.game_over) {
        playGameOverSound();
      } else {
        playSuccessSound();
      }
    } catch (err) {
      playErrorSound();
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') submitCommand(); };

  const resetGame = () => {
    setScreen('intro');
    setArticle('');
    setMessages([]);
    setGameState(null);
    setGameTitle('');
    setSourceUrl(null);
    setInput('');
    setTurnCount(0);
    setError(null);
    setNarrativeComplete(false);
    window.history.replaceState({}, '', window.location.pathname);
  };

  return (
    <div className="app">
      <div className="stars-bg" />

      <div className="content" ref={terminalRef}>

        {screen === 'intro' && (
          <div className="panel panel--centered panel--intro fade-in">
            <div className="title glow-pulse">VK QUEST</div>
            <div className="subtitle">Ett textäventyr från Västerbottens-Kuriren</div>
            <div className="intro-box">
              <p className="intro-text">Kliv in i nyheterna och upplev dem inifrån. Klistra in valfri artikel och förvandla den till ett äventyr!</p>
            </div>
            <button className="btn" onClick={() => { initAudio(); playCommandSound(); setScreen('article'); }}>▶ Starta spelet</button>
            {error && <p className="error error--inline">⚠ {error}</p>}
            <p className="footer-text">VK Media Hackday 2025</p>
          </div>
        )}

        {screen === 'article' && (
          <div className="panel panel--article fade-in">
            <h2 className="section-title">Välj en artikel!</h2>
            <p className="section-subtitle">Klistra in texten från en nyhetsartikel nedan:</p>
            <textarea
              className="article-textarea"
              value={article}
              onChange={e => setArticle(e.target.value)}
              placeholder="Klistra in din nyhetsartikel här..."
            />
            {error && <div className="error">⚠ {error}</div>}
            <div className="btn-group">
              <button className="btn" onClick={startGame} disabled={!article.trim() || loading}>▶ Skapa äventyr</button>
              <button className="btn btn--small" onClick={() => { playCommandSound(); resetGame(); }}>← Tillbaka</button>
            </div>
          </div>
        )}

        {screen === 'loading' && (
          <div className="panel panel--centered panel--loading fade-in">
            <div className="loading-title">Skapar ditt äventyr...</div>
            <div className="loading-dots">
              {[0,1,2,3,4].map(i => <div key={i} className="loading-dot" />)}
            </div>
            <p className="loading-status">Analyserar artikel • Bygger värld • Väcker karaktärer</p>
          </div>
        )}

        {screen === 'ready' && gameState && (
          <div className="panel panel--centered panel--ready fade-in">
            <div className="title glow-pulse">{gameTitle}</div>
            <div className="subtitle">Ditt äventyr väntar</div>
            {gameState.ascii_art && (
              <div className="ascii-container">
                <pre className="ascii-art">{gameState.ascii_art}</pre>
              </div>
            )}
            <button className="btn" onClick={startFromReady}>▶ Starta äventyret</button>
          </div>
        )}

        {screen === 'game' && gameState && (
          <div className="game fade-in">
            <div className="game-header">
              <div className="game-header__top">
                <span className="game-header__title">{gameTitle}</span>
                <span className="game-header__turn">Tur {turnCount}</span>
              </div>
              {gameState.title && gameState.title !== gameTitle && (
                <div className="game-header__scene slide-in">▸ {gameState.title}</div>
              )}
            </div>

            <div className="game-content">
              {gameState.ascii_art && (
                <div className="ascii-container fade-in">
                  <pre className="ascii-art">{gameState.ascii_art}</pre>
                </div>
              )}
              <div className="narrative">
                <Typewriter
                  text={gameState.narrative}
                  speed={7}
                  onComplete={() => setNarrativeComplete(true)}
                />
              </div>
              {gameState.inventory && gameState.inventory.length > 0 && (
                <div className="inventory slide-in">
                  <span className="inventory__label">🎒 Inventarier:</span>
                  <span className="inventory__items">{gameState.inventory.join(' • ')}</span>
                </div>
              )}
              {gameState.npcs && gameState.npcs.length > 0 && (
                <div className="npcs slide-in">
                  <span className="npcs__label">👥 Karaktärer:</span>
                  <div className="npcs__list">
                    {gameState.npcs.map((npc, i) => (
                      <span key={i} className={`npc npc--${npc.attitude}`}>
                        {npc.name}
                        <span className="npc__attitude">
                          {npc.attitude === 'vänlig' && '😊'}
                          {npc.attitude === 'neutral' && '😐'}
                          {npc.attitude === 'misstänksam' && '🤨'}
                          {npc.attitude === 'fientlig' && '😠'}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {gameState.game_over ? (
                <div className="game-over fade-in">
                  <div className="game-over__title glow-pulse">✦ {gameState.game_over_reason || 'SLUT'} ✦</div>
                  <button className="btn" onClick={resetGame}>▶ Spela igen</button>
                </div>
              ) : (
                <>
                  {loading ? (
                    <div className="thinking">
                      <span className="thinking__text">Tänker</span>
                      <span className="thinking__dots">
                        <span className="thinking__dot">.</span>
                        <span className="thinking__dot">.</span>
                        <span className="thinking__dot">.</span>
                      </span>
                    </div>
                  ) : (
                    <div className={narrativeComplete ? 'fade-in' : 'hidden'}>
                      <div className="prompt">{gameState.prompt || 'Vad gör du?'}</div>
                      <div className="quick-actions">
                        <button className="quick-btn" onClick={() => { setInput('Titta omkring'); inputRef.current?.focus(); }}>👁 Titta</button>
                        <button className="quick-btn" onClick={() => { setInput('Undersök '); inputRef.current?.focus(); }}>🔍 Undersök</button>
                        {gameState.npcs?.length > 0 && (
                          <button className="quick-btn" onClick={() => { setInput(`Prata med ${gameState.npcs[0].name}`); inputRef.current?.focus(); }}>💬 Prata</button>
                        )}
                        <button className="quick-btn" onClick={() => { setInput('Gå till '); inputRef.current?.focus(); }}>🚶 Gå</button>
                        <button className="quick-btn" onClick={() => { setInput('Ta '); inputRef.current?.focus(); }}>✋ Ta</button>
                      </div>
                      <div className="command-row">
                        <span className="command-arrow">▸</span>
                        <input
                          ref={inputRef}
                          className="command-input"
                          type="text"
                          value={input}
                          onChange={e => setInput(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Skriv ditt kommando..."
                        />
                      </div>
                      {error && <p className="error error--inline">⚠ {error}</p>}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="game-footer">
              <button className="btn btn--small" onClick={() => { playCommandSound(); resetGame(); }}>✕ Avsluta</button>
              <div className="game-footer__info">
                {sourceUrl && <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="article-link">📰 Läs artikeln</a>}
                <span>VK QUEST • Västerbottens-Kuriren</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
